import { useEffect, useReducer, useRef } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  Camera,
  Check,
  ImagePlus,
  Loader2,
  RotateCcw,
  ScanLine,
  X,
} from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { imageFileValidationError, uploadImagePipeline } from "../../lib/image-upload";
import { useLoggingFlow } from "../../lib/logging-flow";
import { CreditDrawer } from "../CreditDrawer";
import {
  INITIAL_LABEL_FLOW_STATE,
  buildCustomFoodArgs,
  buildLabelReviewPayload,
  labelFlowReducer,
  runNutritionLabelAnalysis,
  runSaveCustomFood,
} from "./label-scan-flow";
import type { LabelFlowState } from "./label-scan-flow";

// Nutrition-label scan flow (plan todo 18a): capture/upload a label photo ->
// analyzeNutritionLabel -> correction form (every field editable, blank =
// unknown) -> "Save as custom food" -> offer "Log a serving" (review with
// dataSource "nutrition-label"). This flow NEVER logs directly and NEVER
// zero-fills blank fields. INSUFFICIENT_CREDITS opens the CreditDrawer;
// ANALYSIS_FAILED offers Retry / Enter manually; a custom-food create
// rejection keeps the form state and shows an inline error.

export interface LabelScanFlowProps {
  readonly demoMode?: boolean;
  readonly onClose: () => void;
  // Test hook: lets renderToStaticMarkup suites render a specific step.
  readonly initialState?: LabelFlowState;
}

const FIELD_ROWS: ReadonlyArray<readonly [keyof LabelFlowState["form"], string, string]> = [
  ["name", "Name", "e.g. Crunchy oat bar"],
  ["brand", "Brand", "Optional"],
  ["servingUnit", "Serving unit", "e.g. bar, serving, g"],
  ["servingWeightG", "Serving weight (g)", "Optional"],
  ["calories", "Calories (kcal)", "Optional"],
  ["proteinG", "Protein (g)", "Optional"],
  ["carbsG", "Carbs (g)", "Optional"],
  ["fatG", "Fat (g)", "Optional"],
  ["fiberG", "Fibre (g)", "Optional"],
];

const NUMERIC_FIELDS: ReadonlySet<string> = new Set([
  "servingWeightG",
  "calories",
  "proteinG",
  "carbsG",
  "fatG",
  "fiberG",
]);

export function LabelScanFlow({ demoMode = false, onClose, initialState }: LabelScanFlowProps) {
  const [state, dispatch] = useReducer(
    labelFlowReducer,
    initialState ?? INITIAL_LABEL_FLOW_STATE,
  );

  const { startFlow } = useLoggingFlow();
  const limits = useQuery(api.images.getUploadLimits, demoMode ? "skip" : {});
  const generateUploadUrl = useMutation(api.images.generateUploadUrl);
  const claimUpload = useMutation(api.images.claimUpload);
  const analyzeNutritionLabel = useAction(api.analyze.analyzeNutritionLabel);
  const createCustomFood = useMutation(api.customFoods.create);

  const libraryInputRef = useRef<HTMLInputElement>(null);
  const captureInputRef = useRef<HTMLInputElement>(null);
  const uploadBusyRef = useRef(false);
  const previewUrlRef = useRef<string | null>(null);

  // Preview object-URL cleanup on unmount (the caller owns revocation).
  useEffect(() => {
    return () => {
      if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    };
  }, []);

  const captureEnabled = !demoMode && limits !== undefined;

  const startUpload = (file: File) => {
    if (limits === undefined) {
      console.warn("[Frontend][LabelScanFlow] Upload blocked; limits not loaded");
      return;
    }
    const validationMessage = imageFileValidationError(file, limits);
    if (validationMessage !== null) {
      console.warn("[Frontend][LabelScanFlow] File rejected by validation", {
        fileName: file.name,
        reason: validationMessage,
      });
      dispatch({ type: "fileRejected", message: validationMessage });
      return;
    }
    if (uploadBusyRef.current) return;
    uploadBusyRef.current = true;
    dispatch({ type: "filePicked", file });
    console.log("[Frontend][LabelScanFlow] Label photo upload started", {
      fileName: file.name,
      fileSize: file.size,
    });
    void uploadImagePipeline(file, { generateUploadUrl, claimUpload })
      .then(({ storageId, previewUrl }) => {
        if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = previewUrl;
        dispatch({ type: "uploadSucceeded", storageId, previewUrl });
        console.log("[Frontend][LabelScanFlow] Label photo upload claimed", { fileName: file.name });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        dispatch({ type: "uploadFailed", message });
        console.error("[Frontend][LabelScanFlow] Label photo upload failed", {
          fileName: file.name,
          error: message,
        });
      })
      .finally(() => {
        uploadBusyRef.current = false;
      });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    startUpload(file);
  };

  const startAnalysis = () => {
    const storageId = state.storageId;
    if (storageId === null) return;
    dispatch({ type: "analyzeStarted" });
    console.log("[Frontend][LabelScanFlow] Label analysis requested");
    void runNutritionLabelAnalysis(analyzeNutritionLabel, storageId as Id<"_storage">).then(
      (outcome) => {
        if (outcome.ok) {
          dispatch({ type: "analyzeSucceeded", analysis: outcome.analysis });
          return;
        }
        dispatch({ type: "analyzeFailed", message: outcome.message, credits: outcome.credits });
      },
    );
  };

  const handleSave = () => {
    const built = buildCustomFoodArgs(state.form, state.additionalNutrientsJson);
    if (!built.ok) {
      console.warn("[Frontend][LabelScanFlow] Custom food validation failed", { reason: built.error });
      dispatch({ type: "saveFailed", message: built.error });
      return;
    }
    dispatch({ type: "saveStarted" });
    console.log("[Frontend][LabelScanFlow] Saving custom food from label", { name: built.args.name });
    void runSaveCustomFood(createCustomFood, built.args).then((outcome) => {
      if (outcome.ok) {
        dispatch({ type: "saveSucceeded" });
        return;
      }
      dispatch({ type: "saveFailed", message: outcome.error });
    });
  };

  const handleLogServing = () => {
    const payload = buildLabelReviewPayload(state.form);
    console.log("[Frontend][LabelScanFlow] Routing saved label food to review", {
      name: state.form.name.trim(),
    });
    startFlow("review", payload);
  };

  const handleEnterManually = () => {
    console.log("[Frontend][LabelScanFlow] Analysis declined; opening blank correction form");
    dispatch({ type: "enterManually" });
  };

  const handlePickDifferent = () => {
    if (previewUrlRef.current !== null) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    dispatch({ type: "pickDifferentPhoto" });
  };

  const optionButtonClass =
    "flex min-h-[56px] w-full items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-base font-medium text-text transition-all duration-200 ease-out active:scale-[0.98] active:bg-muted disabled:opacity-50 select-none";
  const primaryButtonClass =
    "flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 text-base font-semibold text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] disabled:opacity-50 select-none";
  const secondaryButtonClass =
    "flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-base font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none";
  const inputClass =
    "w-full rounded-xl border border-border bg-muted px-4 py-3 text-base text-text placeholder:text-text-muted caret-accent focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent";

  return (
    <div data-testid="label-flow" className="fixed inset-0 z-40 flex flex-col bg-muted">
      <header className="w-full shrink-0 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-lg font-semibold text-text">Scan nutrition label</h1>
            <p className="text-xs text-text-muted">Check the values, then save the food</p>
          </div>
          <button
            type="button"
            data-testid="label-flow-close"
            aria-label="Close label scan"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div className="mx-auto flex max-w-xl flex-col gap-3 px-4 py-4">
          {state.phase === "pick" && (
            <>
              {state.errorMessage !== null && (
                <p
                  role="alert"
                  data-testid="label-flow-error"
                  className="rounded-2xl bg-danger/10 px-4 py-3 text-sm font-medium text-danger"
                >
                  {state.errorMessage}
                </p>
              )}
              <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                <p className="text-sm text-text-muted">
                  Photograph the nutrition label on the package. We read the values, you confirm them.
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  <button
                    type="button"
                    data-testid="label-flow-take-photo"
                    disabled={!captureEnabled}
                    onClick={() => captureInputRef.current?.click()}
                    className={optionButtonClass}
                  >
                    <Camera size={22} className="text-text-muted" aria-hidden="true" />
                    Take label photo
                  </button>
                  <button
                    type="button"
                    data-testid="label-flow-choose-library"
                    disabled={!captureEnabled}
                    onClick={() => libraryInputRef.current?.click()}
                    className={optionButtonClass}
                  >
                    <ImagePlus size={22} className="text-text-muted" aria-hidden="true" />
                    Upload from library
                  </button>
                </div>
                {demoMode && (
                  <p data-testid="label-flow-demo-note" className="mt-3 text-xs text-text-muted">
                    Demo mode — sign in to scan and save nutrition labels.
                  </p>
                )}
              </div>
            </>
          )}

          {state.phase === "uploading" && (
            <div
              data-testid="label-flow-uploading"
              className="flex items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-4 shadow-sm"
            >
              <Loader2 size={22} className="animate-spin text-accent" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text">Uploading photo…</p>
                <p className="truncate text-xs text-text-muted">{state.fileName ?? "label photo"}</p>
              </div>
            </div>
          )}

          {state.phase === "upload-error" && (
            <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <p role="alert" data-testid="label-flow-error" className="text-sm font-medium text-danger">
                Could not upload {state.fileName ?? "the photo"}. {state.errorMessage ?? ""}
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  data-testid="label-flow-retry-upload"
                  onClick={() => {
                    if (state.file !== null) startUpload(state.file);
                  }}
                  className={primaryButtonClass}
                >
                  <RotateCcw size={18} aria-hidden="true" />
                  Retry upload
                </button>
                <button
                  type="button"
                  data-testid="label-flow-change-photo"
                  onClick={handlePickDifferent}
                  className="min-h-[44px] text-sm font-medium text-text-muted transition-opacity active:opacity-80 select-none"
                >
                  Choose a different photo
                </button>
              </div>
            </div>
          )}

          {state.phase === "confirm" && (
            <div className="flex flex-col gap-3">
              {state.previewUrl !== null && (
                <img
                  src={state.previewUrl}
                  alt="Selected nutrition label"
                  data-testid="label-flow-preview"
                  className="aspect-[4/3] w-full rounded-2xl border border-border object-cover"
                />
              )}
              <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                <p className="text-sm text-text-muted">
                  Make sure the label is sharp and fully visible before we read it.
                </p>
                <button
                  type="button"
                  data-testid="label-flow-analyze"
                  onClick={startAnalysis}
                  className={`mt-3 ${primaryButtonClass}`}
                >
                  <ScanLine size={18} aria-hidden="true" />
                  Read nutrition label
                </button>
                <button
                  type="button"
                  data-testid="label-flow-change-photo"
                  onClick={handlePickDifferent}
                  className="mt-2 min-h-[44px] w-full text-sm font-medium text-text-muted transition-opacity active:opacity-80 select-none"
                >
                  Choose a different photo
                </button>
              </div>
            </div>
          )}

          {state.phase === "analyzing" && (
            <div
              data-testid="label-flow-analyzing"
              role="status"
              className="rounded-2xl border border-border bg-surface p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <Loader2 size={22} className="animate-spin text-accent" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-text">Reading the label…</p>
                  <p className="text-xs text-text-muted">Extracting serving and nutrition values</p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2" aria-hidden="true">
                <div className="h-10 animate-pulse rounded-xl bg-muted" />
                <div className="h-10 animate-pulse rounded-xl bg-muted" style={{ animationDelay: "150ms" }} />
                <div className="h-10 w-2/3 animate-pulse rounded-xl bg-muted" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {state.phase === "analysis-error" && (
            <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <p role="alert" data-testid="label-flow-error" className="text-sm font-medium text-danger">
                We could not read this label. Your photo is still here — try again or type the values yourself.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  data-testid="label-flow-retry-analysis"
                  onClick={startAnalysis}
                  className={primaryButtonClass}
                >
                  <RotateCcw size={18} aria-hidden="true" />
                  Retry
                </button>
                <button
                  type="button"
                  data-testid="label-flow-enter-manual"
                  onClick={handleEnterManually}
                  className={secondaryButtonClass}
                >
                  Enter manually
                </button>
              </div>
            </div>
          )}

          {state.phase === "correct" && (
            <div data-testid="label-flow-form" className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <p className="text-sm text-text-muted">
                Check every value against the package. Leave a field blank when it is unknown — blanks are never saved as zero.
              </p>
              <div className="mt-3 flex flex-col gap-3">
                {FIELD_ROWS.map(([field, label, placeholder]) => (
                  <label key={field} className="flex flex-col gap-1.5" data-testid={`label-flow-field-${field}`}>
                    <span className="text-sm font-medium text-text">{label}</span>
                    <input
                      type={NUMERIC_FIELDS.has(field) ? "number" : "text"}
                      {...(NUMERIC_FIELDS.has(field) ? { min: "0", step: "0.1", inputMode: "decimal" as const } : {})}
                      data-testid={`label-flow-input-${field}`}
                      value={state.form[field]}
                      onChange={(event) => dispatch({ type: "fieldChanged", field, value: event.target.value })}
                      placeholder={placeholder}
                      className={inputClass}
                    />
                  </label>
                ))}
              </div>
              {state.additionalNutrientsJson !== null && (
                <p data-testid="label-flow-additional-note" className="mt-3 text-xs text-text-muted">
                  Extra nutrients from the label are saved with this food.
                </p>
              )}
              {state.saveError !== null && (
                <p
                  role="alert"
                  data-testid="label-flow-save-error"
                  className="mt-3 rounded-xl bg-danger/10 px-3 py-2 text-sm font-medium text-danger"
                >
                  {state.saveError}
                </p>
              )}
              <button
                type="button"
                data-testid="label-flow-save"
                disabled={state.saving}
                aria-busy={state.saving}
                onClick={handleSave}
                className={`mt-4 ${primaryButtonClass}`}
              >
                {state.saving && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
                {state.saving ? "Saving…" : "Save as custom food"}
              </button>
              <button
                type="button"
                data-testid="label-flow-retake"
                onClick={handlePickDifferent}
                className="mt-2 min-h-[44px] w-full text-sm font-medium text-text-muted transition-opacity active:opacity-80 select-none"
              >
                Scan a different label
              </button>
            </div>
          )}

          {state.phase === "saved" && (
            <div data-testid="label-flow-saved" className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-light text-accent">
                  <Check size={20} strokeWidth={2.5} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text">Saved to your custom foods</p>
                  <p className="text-xs text-text-muted">{state.form.name.trim()}</p>
                </div>
              </div>
              <button
                type="button"
                data-testid="label-flow-log-serving"
                onClick={handleLogServing}
                className={`mt-4 ${primaryButtonClass}`}
              >
                Log a serving
              </button>
              <button
                type="button"
                data-testid="label-flow-done"
                onClick={onClose}
                className={`mt-2 ${secondaryButtonClass}`}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </main>

      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        data-testid="label-flow-file-input"
        onChange={handleInputChange}
        className="hidden"
      />
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        data-testid="label-flow-camera-input"
        onChange={handleInputChange}
        className="hidden"
      />

      {state.creditDrawerOpen && (
        <CreditDrawer onClose={() => dispatch({ type: "creditDrawerClosed" })} />
      )}
    </div>
  );
}

export default LabelScanFlow;
